/**
 * Skyyrizon Daily Focus Rotator
 *   - Selector pills switch the active product panel (tabs pattern).
 *   - Optional autoplay (default off), pauses on hover/focus/offscreen.
 *   - Keyboard: arrow keys move between pills.
 *   - Subtle 3D tilt on the media (desktop, fine pointer).
 *   - Lazy demo video with play/pause toggle, muted, offscreen-safe.
 *   - Quick add via /cart/add.js + shared toast + cart bubble sync.
 * Analytics fire via the global bridge (data-sky-event on pills/CTAs/forms).
 */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var TOAST_MS = 1800, DONE_MS = 1600;

  function initSection(root) {
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-sky-df-pill]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-sky-df-panel]'));
    if (panels.length === 0) return;

    var current = 0;
    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduced && panels.length > 1;
    var interval = (parseInt(root.getAttribute('data-autoplay-seconds'), 10) || 6) * 1000;
    var timer = null, hovered = false, visible = true;

    function show(idx) {
      var n = ((idx % panels.length) + panels.length) % panels.length;
      panels.forEach(function (p, i) {
        var active = i === n;
        p.classList.toggle('is-active', active);
        if (active) p.removeAttribute('hidden'); else p.setAttribute('hidden', '');
        // pause any video in the panel we leave
        if (!active) { var v = p.querySelector('[data-sky-df-video]'); if (v && !v.paused) v.pause(); }
      });
      pills.forEach(function (pill, i) {
        pill.classList.toggle('is-active', i === n);
        pill.setAttribute('aria-selected', i === n ? 'true' : 'false');
      });
      current = n;
    }

    pills.forEach(function (pill, i) {
      pill.addEventListener('click', function () { show(i); restart(); });
      pill.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); var t = pills[(i + 1) % pills.length]; t.focus(); show((i + 1) % pills.length); restart(); }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); var pv = (i - 1 + pills.length) % pills.length; pills[pv].focus(); show(pv); restart(); }
      });
    });

    function tick() { if (!hovered && visible) show(current + 1); }
    function start() { if (autoplay && !timer) timer = window.setInterval(tick, interval); }
    function stop() { if (timer) { window.clearInterval(timer); timer = null; } }
    function restart() { stop(); start(); }

    root.addEventListener('mouseenter', function () { hovered = true; stop(); });
    root.addEventListener('mouseleave', function () { hovered = false; start(); });
    root.addEventListener('focusin', function () { hovered = true; stop(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { visible = e.isIntersecting; if (visible) start(); else stop(); });
      }, { threshold: 0.2 }).observe(root);
    }
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });

    // Tilt per panel media
    if (!reduced && !coarse && root.getAttribute('data-tilt') === 'true') {
      root.querySelectorAll('[data-sky-df-tilt]').forEach(function (media) {
        var inner = media.querySelector('[data-sky-df-tilt-inner]');
        if (!inner) return;
        var raf = null, rx = 0, ry = 0;
        function apply() { raf = null; inner.style.transform = 'rotateX(' + ry.toFixed(2) + 'deg) rotateY(' + rx.toFixed(2) + 'deg)'; }
        media.addEventListener('mousemove', function (e) {
          var r = media.getBoundingClientRect();
          rx = ((e.clientX - r.left) / r.width - 0.5) * 8;
          ry = -((e.clientY - r.top) / r.height - 0.5) * 8;
          if (raf === null) raf = window.requestAnimationFrame(apply);
        });
        media.addEventListener('mouseleave', function () { rx = 0; ry = 0; if (raf === null) raf = window.requestAnimationFrame(apply); });
      });
    }

    // Demo video toggles
    root.querySelectorAll('[data-sky-df-play]').forEach(function (btn) {
      var panel = btn.closest('[data-sky-df-panel]');
      var video = panel ? panel.querySelector('[data-sky-df-video]') : null;
      var label = btn.querySelector('[data-sky-df-play-label]');
      if (!video) return;
      btn.addEventListener('click', function () {
        if (video.paused) {
          video.querySelectorAll('source[data-src]').forEach(function (s) { s.src = s.getAttribute('data-src'); s.removeAttribute('data-src'); });
          if (video.getAttribute('data-loaded') !== 'true') { video.load(); video.setAttribute('data-loaded', 'true'); }
          video.muted = true;
          var p = video.play(); if (p && p.catch) p.catch(function () {});
          btn.setAttribute('aria-pressed', 'true'); if (label) label.textContent = 'Pause';
          stop(); // don't rotate away while watching
        } else {
          video.pause();
          btn.setAttribute('aria-pressed', 'false'); if (label) label.textContent = 'Watch it work';
          start();
        }
      });
    });

    // Quick add
    root.querySelectorAll('[data-sky-df-add]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;
        var orig = btn.textContent;
        btn.disabled = true; btn.textContent = 'Adding…';
        var routes = (window.Shopify && window.Shopify.routes) || {};
        var url = (routes.cart_add_url || '/cart/add.js') + '?sections=cart-icon-bubble,cart-live-region-text';
        fetch(url, { method: 'POST', credentials: 'include', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) })
          .then(function (r) { if (!r.ok) return r.json().then(function (x) { throw x; }); return r.json(); })
          .then(function (cart) {
            btn.textContent = 'Added ✓';
            toast(root, 'Added to cart');
            syncCart(cart);
            window.setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, DONE_MS);
          })
          .catch(function (err) {
            btn.textContent = (err && err.description) || 'Could not add';
            window.setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, DONE_MS);
          });
      });
    });

    start();
  }

  function toast(scope, msg) {
    var t = scope.querySelector('[data-sky-cc-toast]') || document.querySelector('[data-sky-cc-toast]');
    if (!t) return;
    var txt = t.querySelector('.sky-cc-toast__text'); if (txt) txt.textContent = msg;
    t.classList.add('is-visible');
    window.clearTimeout(t._timer);
    t._timer = window.setTimeout(function () { t.classList.remove('is-visible'); }, TOAST_MS);
  }

  function syncCart(cart) {
    if (!cart || !cart.sections) return;
    ['cart-icon-bubble', 'cart-live-region-text'].forEach(function (key) {
      if (!cart.sections[key]) return;
      try {
        var doc = new DOMParser().parseFromString(cart.sections[key], 'text/html');
        var next = doc.querySelector('#shopify-section-' + key);
        var cur = document.getElementById('shopify-section-' + key);
        if (next && cur) cur.replaceWith(next);
      } catch (e) {}
    });
    if (window.PUB_SUB_EVENTS && window.publish) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'skyyrizon-daily-focus', cartData: cart });
  }

  function init() { document.querySelectorAll('[data-sky-df]').forEach(initSection); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
